import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RichTextEditor from '../RichTextEditor/RichTextEditor';
import AmpOfTheDayAdmin from './AmpOfTheDayAdmin';
import AmpVideosAdmin from './AmpVideosAdmin';
import axios from 'axios';
import { USERS_API_URL } from '../../config/apiConfig';

const api = axios.create({ baseURL: USERS_API_URL });

// Helper to detect auth/session errors from API responses
const getAuthError = (error) => {
  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  if (status === 401 || code === 'TOKEN_EXPIRED' || code === 'SESSION_EXPIRED') {
    return 'Your session has expired. Please log out and log back in.';
  }
  if (status === 403) {
    return 'Access denied. You may need to log out and log back in.';
  }
  return null;
};

// ─── Pending Review tab ────────────────────────────────────────────────────────
const PendingReviewAdmin = ({ api }) => {
  const [amps, setAmps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const loadAmps = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/amplifiers/pending-review?pageSize=200');
      setAmps(res.data.data || []);
    } catch (err) {
      setError('Failed to load pending amps.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadAmps(); }, [loadAmps]);

  const handleApprove = async (amp) => {
    setApprovingId(amp.amplifier_id);
    try {
      await api.patch(`/amplifiers/${amp.amplifier_id}/approve`);
      setAmps(prev => prev.filter(a => a.amplifier_id !== amp.amplifier_id));
    } catch (err) {
      alert('Failed to approve amp. Please try again.');
      console.error(err);
    } finally {
      setApprovingId(null);
    }
  };

  const handleDelete = async (amp) => {
    if (!window.confirm(`Delete "${amp.name || amp.model}" (ID ${amp.amplifier_id})? This cannot be undone.`)) return;
    setDeletingId(amp.amplifier_id);
    try {
      await api.delete(`/amplifiers/${amp.amplifier_id}`);
      setAmps(prev => prev.filter(a => a.amplifier_id !== amp.amplifier_id));
    } catch (err) {
      alert('Failed to delete amp. Please try again.');
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <div style={{ padding: '2rem', color: '#666' }}>Loading pending amps…</div>;
  if (error) return <div style={{ padding: '2rem', color: '#dc3545' }}>{error}</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h3 style={{ margin: 0 }}>Pending Review ({amps.length})</h3>
        <button onClick={loadAmps} style={{ background: 'none', border: '1px solid #ccc', borderRadius: '6px', padding: '0.4rem 0.9rem', cursor: 'pointer', fontSize: '0.9rem' }}>↻ Refresh</button>
      </div>
      {amps.length === 0 ? (
        <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '2rem', textAlign: 'center', color: '#666' }}>
          No amps pending review.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {amps.map(amp => (
            <div key={amp.amplifier_id} style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: '8px', padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
              <div>
                <strong>{amp.manufacturer_name}</strong>
                {' — '}
                <a href={`/amps/${amp.manufacturer_slug}/${amp.slug}`} target="_blank" rel="noreferrer" style={{ color: '#007bff' }}>
                  {amp.model || amp.name}
                  {amp.start_year ? ` (${amp.start_year}${amp.end_year && amp.end_year !== amp.start_year ? `–${amp.end_year}` : ''})` : ''}
                </a>
                <span style={{ marginLeft: '1rem', color: '#aaa', fontSize: '0.8rem' }}>ID {amp.amplifier_id}</span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => handleApprove(amp)}
                  disabled={approvingId === amp.amplifier_id || deletingId === amp.amplifier_id}
                  style={{
                    background: '#28a745', color: 'white', border: 'none',
                    padding: '0.4rem 1rem', borderRadius: '6px',
                    cursor: (approvingId === amp.amplifier_id || deletingId === amp.amplifier_id) ? 'not-allowed' : 'pointer',
                    fontWeight: '600', fontSize: '0.9rem', whiteSpace: 'nowrap',
                    opacity: approvingId === amp.amplifier_id ? 0.7 : 1
                  }}
                >
                  {approvingId === amp.amplifier_id ? 'Approving…' : '✓ Approve'}
                </button>
                <button
                  onClick={() => handleDelete(amp)}
                  disabled={deletingId === amp.amplifier_id || approvingId === amp.amplifier_id}
                  style={{
                    background: '#dc3545', color: 'white', border: 'none',
                    padding: '0.4rem 1rem', borderRadius: '6px',
                    cursor: (deletingId === amp.amplifier_id || approvingId === amp.amplifier_id) ? 'not-allowed' : 'pointer',
                    fontWeight: '600', fontSize: '0.9rem', whiteSpace: 'nowrap',
                    opacity: deletingId === amp.amplifier_id ? 0.7 : 1
                  }}
                >
                  {deletingId === amp.amplifier_id ? 'Deleting…' : '✕ Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SiteAdminScreen = () => {
  const { isAdmin, token, isReadOnly, canAccessScreen } = useAuth();
  const [activeTab, setActiveTab] = useState('content');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Multiple What's New sections state
  const [whatsNewSections, setWhatsNewSections] = useState([]);
  const [editingSection, setEditingSection] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Site settings state
  const [siteSettings, setSiteSettings] = useState([]);
  const [editedSettings, setEditedSettings] = useState({});
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);  const [settingsError, setSettingsError] = useState(null);
  // Check permissions for this screen
  const screenPath = '/site-admin';
  const readOnlyMode = isReadOnly(screenPath);
  const hasScreenAccess = canAccessScreen(screenPath);

  // Set up axios interceptor for authentication
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  // Load What's New sections on component mount
  useEffect(() => {
    loadWhatsNewSections();
  }, []);
  
  // Load site settings when settings tab is activated
  useEffect(() => {
    if (activeTab === 'settings') {
      loadSiteSettings();
    }
  }, [activeTab]);

  const loadWhatsNewSections = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/whats-new-sections');
      setWhatsNewSections(response.data.sections);
    } catch (error) {
      console.error('Error loading What\'s New sections:', error);
      setWhatsNewSections([]);
    } finally {
      setLoading(false);
    }
  };
  
  const loadSiteSettings = async () => {
    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const response = await api.get('/admin/site-settings');
      setSiteSettings(response.data.settings || []);
      // Initialize edited values
      const initialEdited = {};
      (response.data.settings || []).forEach(s => {
        initialEdited[s.setting_key] = s.setting_value;
      });
      setEditedSettings(initialEdited);
    } catch (error) {
      console.error('Error loading site settings:', error);
      const authErr = getAuthError(error);
      if (authErr) {
        setSettingsError(authErr);
      } else if (!error.response) {
        setSettingsError('Cannot connect to the API server. Is it running?');
      } else {
        setSettingsError(error.response?.data?.error || error.message || 'Failed to load settings');
      }
      setSiteSettings([]);
    } finally {
      setSettingsLoading(false);
    }
  };
  
  const handleSettingChange = (key, value) => {
    setEditedSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    setMessage(null);
    
    try {
      // Find changed settings
      const changedSettings = [];
      siteSettings.forEach(setting => {
        if (editedSettings[setting.setting_key] !== setting.setting_value) {
          changedSettings.push({
            key: setting.setting_key,
            value: editedSettings[setting.setting_key]
          });
        }
      });
      
      if (changedSettings.length === 0) {
        setMessage({ type: 'info', text: 'No changes to save.' });
        setTimeout(() => setMessage(null), 3000);
        setSettingsSaving(false);
        return;
      }
      
      await api.put('/admin/site-settings', { settings: changedSettings });
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      await loadSiteSettings(); // Reload to get fresh values
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      const authMsg = getAuthError(error);
      setMessage({ type: 'error', text: authMsg || 'Error saving settings. Please try again.' });
    } finally {
      setSettingsSaving(false);
    }
  };
  
  // Group settings by category
  const groupedSettings = siteSettings.reduce((acc, setting) => {
    const category = setting.setting_category || 'general';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(setting);
    return acc;
  }, {});
  
  const categoryLabels = {
    general: 'General Settings',
    email: 'Email Settings',
    billing: 'Billing & Payments'
  };

  const handleAddSection = () => {
    setEditingSection({ 
      title: '', 
      content_html: '<p>Enter your content here...</p>', 
      display_order: (whatsNewSections.length + 1)
    });
    setShowAddForm(true);
  };

  const handleEditSection = (section) => {
    console.log('handleEditSection called with:', section);
    setEditingSection({ ...section });
    setShowAddForm(true);
    // Scroll to form after a brief delay
    setTimeout(() => {
      const formElement = document.querySelector('[data-edit-form]');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleSaveSection = async () => {
    if (!editingSection.title.trim()) {
      setMessage({ type: 'error', text: 'Title is required' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      if (editingSection.section_id) {
        // Update existing section
        await api.put(`/admin/whats-new-sections/${editingSection.section_id}`, {
          title: editingSection.title,
          content_html: editingSection.content_html,
          display_order: editingSection.display_order,
          is_active: editingSection.is_active !== false
        });
        setMessage({ type: 'success', text: 'Section updated successfully!' });
      } else {
        // Create new section
        await api.post('/admin/whats-new-sections', {
          title: editingSection.title,
          content_html: editingSection.content_html,
          display_order: editingSection.display_order
        });
        setMessage({ type: 'success', text: 'Section created successfully!' });
      }
      
      setShowAddForm(false);
      setEditingSection(null);
      await loadWhatsNewSections();
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving section:', error);
      const authMsg = getAuthError(error);
      setMessage({ type: 'error', text: authMsg || 'Error saving section. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Are you sure you want to delete this section?')) {
      return;
    }

    try {
      await api.delete(`/admin/whats-new-sections/${sectionId}`);
      setMessage({ type: 'success', text: 'Section deleted successfully!' });
      await loadWhatsNewSections();
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting section:', error);
      const authMsg = getAuthError(error);
      setMessage({ type: 'error', text: authMsg || 'Error deleting section. Please try again.' });
    }
  };

  const handleCancelEdit = () => {
    setShowAddForm(false);
    setEditingSection(null);
  };

  // Check if user has access to this screen
  if (!hasScreenAccess && !isAdmin) {
    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '8px',
        color: '#856404'
      }}>
        <h2>Access Denied</h2>
        <p>You do not have permission to access Site Administration.</p>
        <p>Contact your administrator to request access.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ color: '#333', marginBottom: '0.5rem' }}>Site Administration</h1>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>
          Manage site-wide settings and content
        </p>
      </div>

      {/* Message Display */}
      {message && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          borderRadius: '4px',
          backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
          border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
          color: message.type === 'success' ? '#155724' : '#721c24'
        }}>
          {message.text}
        </div>
      )}

      {/* Navigation Tabs */}
      <div style={{ marginBottom: '2rem' }}>
        <nav style={{ 
          display: 'flex', 
          borderBottom: '1px solid #dee2e6',
          gap: '2rem'
        }}>
          <button
            onClick={() => setActiveTab('content')}
            style={{
              background: 'none',
              border: 'none',
              padding: '1rem 0',
              borderBottom: activeTab === 'content' ? '2px solid #007bff' : '2px solid transparent',
              color: activeTab === 'content' ? '#007bff' : '#666',
              cursor: 'pointer',
              fontWeight: activeTab === 'content' ? 'bold' : 'normal',
              fontSize: '1rem'
            }}
          >
            Content Management
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            style={{
              background: 'none',
              border: 'none',
              padding: '1rem 0',
              borderBottom: activeTab === 'settings' ? '2px solid #007bff' : '2px solid transparent',
              color: activeTab === 'settings' ? '#007bff' : '#666',
              cursor: 'pointer',
              fontWeight: activeTab === 'settings' ? 'bold' : 'normal',
              fontSize: '1rem'
            }}
          >
            Site Settings
          </button>
          <button
            onClick={() => setActiveTab('amp-of-the-day')}
            style={{
              background: 'none',
              border: 'none',
              padding: '1rem 0',
              borderBottom: activeTab === 'amp-of-the-day' ? '2px solid #007bff' : '2px solid transparent',
              color: activeTab === 'amp-of-the-day' ? '#007bff' : '#666',
              cursor: 'pointer',
              fontWeight: activeTab === 'amp-of-the-day' ? 'bold' : 'normal',
              fontSize: '1rem'
            }}
          >
            Amp of the Day
          </button>
          <button
            onClick={() => setActiveTab('amp-videos')}
            style={{
              background: 'none',
              border: 'none',
              padding: '1rem 0',
              borderBottom: activeTab === 'amp-videos' ? '2px solid #007bff' : '2px solid transparent',
              color: activeTab === 'amp-videos' ? '#007bff' : '#666',
              cursor: 'pointer',
              fontWeight: activeTab === 'amp-videos' ? 'bold' : 'normal',
              fontSize: '1rem'
            }}
          >
            Amp Videos
          </button>
          <button
            onClick={() => setActiveTab('pending-review')}
            style={{
              background: 'none',
              border: 'none',
              padding: '1rem 0',
              borderBottom: activeTab === 'pending-review' ? '2px solid #007bff' : '2px solid transparent',
              color: activeTab === 'pending-review' ? '#007bff' : '#666',
              cursor: 'pointer',
              fontWeight: activeTab === 'pending-review' ? 'bold' : 'normal',
              fontSize: '1rem'
            }}
          >
            Pending Review
          </button>
        </nav>
      </div>

      {/* Content Management Tab */}
      {activeTab === 'content' && (
        <div>
          {/* What's New Sections Management */}
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '1.5rem',
            borderRadius: '8px',
            marginBottom: '2rem',
            border: '1px solid #dee2e6'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ marginTop: 0, marginBottom: 0 }}>What's New Sections</h2>
              {!readOnlyMode && (
                <button
                  onClick={handleAddSection}
                  style={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  + Add New Section
                </button>
              )}
            </div>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              Manage the "What's New" sections that appear on the home page. Each section can have its own title and content.
              You can reorder sections and add rich text formatting with images.
            </p>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <p>Loading sections...</p>
              </div>
            ) : (
              <div>
                {whatsNewSections.length === 0 ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '2rem',
                    backgroundColor: '#fff',
                    border: '1px dashed #dee2e6',
                    borderRadius: '4px'
                  }}>
                    <p style={{ color: '#666', margin: 0 }}>No sections created yet. Click "Add New Section" to get started.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {whatsNewSections.map((section) => (
                      <div key={section.section_id} style={{
                        backgroundColor: '#fff',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        padding: '1rem'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                          <div>
                            <h4 style={{ margin: '0 0 0.25rem 0', color: '#333' }}>
                              {section.title}
                            </h4>
                            <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>
                              Order: {section.display_order} | Last updated: {new Date(section.updated_at).toLocaleString()}
                            </p>
                          </div>
                          {!readOnlyMode && (
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                onClick={() => handleEditSection(section)}
                                style={{
                                  backgroundColor: '#007bff',
                                  color: 'white',
                                  border: 'none',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  fontSize: '0.8rem'
                                }}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteSection(section.section_id)}
                                style={{
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  fontSize: '0.8rem'
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                        <div style={{ 
                          fontSize: '0.9rem',
                          color: '#666',
                          borderTop: '1px solid #eee',
                          paddingTop: '0.5rem',
                          marginTop: '0.5rem'
                        }}>
                          <div dangerouslySetInnerHTML={{ __html: section.content_html }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Add/Edit Section Form */}
          {showAddForm && editingSection && (
            <div 
              data-edit-form
              style={{
              backgroundColor: '#fff',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid #dee2e6',
              marginBottom: '2rem'
            }}>
              <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>
                {editingSection.section_id ? 'Edit Section' : 'Add New Section'}
              </h3>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: 'bold',
                  color: '#333'
                }}>
                  Section Title:
                </label>
                <input
                  type="text"
                  value={editingSection.title}
                  onChange={(e) => setEditingSection(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter section title..."
                  style={{
                    width: '100%',
                    maxWidth: '400px',
                    padding: '0.75rem',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                  disabled={readOnlyMode}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: 'bold',
                  color: '#333'
                }}>
                  Display Order:
                </label>
                <input
                  type="number"
                  value={editingSection.display_order}
                  onChange={(e) => setEditingSection(prev => ({ ...prev, display_order: parseInt(e.target.value) || 1 }))}
                  style={{
                    width: '100px',
                    padding: '0.5rem',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                  disabled={readOnlyMode}
                  min="1"
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: 'bold',
                  color: '#333'
                }}>
                  Content:
                </label>
                <RichTextEditor
                  value={editingSection.content_html}
                  onChange={(content) => setEditingSection(prev => ({ ...prev, content_html: content }))}
                  placeholder="Enter your content here. You can use rich text formatting, add images, and include links."
                  readOnly={readOnlyMode}
                />
              </div>

              {!readOnlyMode && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                  <button
                    onClick={handleCancelEdit}
                    style={{
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveSection}
                    disabled={saving}
                    style={{
                      backgroundColor: saving ? '#6c757d' : '#28a745',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '4px',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    {saving ? 'Saving...' : (editingSection.section_id ? 'Update Section' : 'Create Section')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Site Settings Tab */}
      {activeTab === 'settings' && (
        <div>
          {settingsLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>Loading settings...</p>
            </div>
          ) : siteSettings.length === 0 ? (
            <div style={{
              backgroundColor: settingsError ? '#f8d7da' : '#fff3cd',
              padding: '2rem',
              borderRadius: '8px',
              textAlign: 'center',
              border: `1px solid ${settingsError ? '#f5c6cb' : '#ffeeba'}`
            }}>
              <h3>{settingsError ? '⚠️ Error Loading Settings' : 'No Settings Found'}</h3>
              <p style={{ color: settingsError ? '#721c24' : '#856404' }}>
                {settingsError || 'No site settings have been configured yet. Run the database migration to seed default settings.'}
              </p>
              {settingsError && (
                <button
                  onClick={loadSiteSettings}
                  style={{
                    marginTop: '1rem',
                    padding: '0.5rem 1rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  🔄 Retry
                </button>
              )}
            </div>
          ) : (
            <div>
              {/* Save Button at Top */}
              {!readOnlyMode && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  marginBottom: '1.5rem' 
                }}>
                  <button
                    onClick={handleSaveSettings}
                    disabled={settingsSaving}
                    style={{
                      backgroundColor: settingsSaving ? '#6c757d' : '#28a745',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '4px',
                      cursor: settingsSaving ? 'not-allowed' : 'pointer',
                      fontSize: '1rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {settingsSaving ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              )}
              
              {/* Settings by Category */}
              {Object.entries(groupedSettings).map(([category, settings]) => (
                <div 
                  key={category}
                  style={{
                    backgroundColor: '#f8f9fa',
                    padding: '1.5rem',
                    borderRadius: '8px',
                    marginBottom: '1.5rem',
                    border: '1px solid #dee2e6'
                  }}
                >
                  <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#333' }}>
                    {categoryLabels[category] || `${category.charAt(0).toUpperCase() + category.slice(1)}`}
                  </h3>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                    {settings.map(setting => (
                      <div key={setting.setting_key} style={{
                        backgroundColor: '#fff',
                        padding: '1rem',
                        borderRadius: '4px',
                        border: '1px solid #e9ecef'
                      }}>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '0.5rem',
                          fontWeight: 'bold',
                          color: '#333'
                        }}>
                          {setting.setting_label}
                        </label>
                        {setting.setting_description && (
                          <p style={{ 
                            margin: '0 0 0.75rem 0', 
                            fontSize: '0.85rem', 
                            color: '#666' 
                          }}>
                            {setting.setting_description}
                          </p>
                        )}
                        
                        {setting.setting_type === 'boolean' ? (
                          <select
                            value={editedSettings[setting.setting_key] || 'false'}
                            onChange={(e) => handleSettingChange(setting.setting_key, e.target.value)}
                            disabled={readOnlyMode || setting.is_sensitive}
                            style={{
                              padding: '0.5rem',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              fontSize: '1rem',
                              width: '150px'
                            }}
                          >
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </select>
                        ) : setting.setting_type === 'boolean_toggle' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <select
                              value={editedSettings[setting.setting_key] || 'test'}
                              onChange={(e) => handleSettingChange(setting.setting_key, e.target.value)}
                              disabled={readOnlyMode}
                              style={{
                                padding: '0.5rem',
                                border: '1px solid #ced4da',
                                borderRadius: '4px',
                                fontSize: '1rem',
                                width: '150px'
                              }}
                            >
                              <option value="test">🧪 Test (Sandbox)</option>
                              <option value="live">🟢 Live (Production)</option>
                            </select>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '12px',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              background: (editedSettings[setting.setting_key] || 'test') === 'live' ? '#e74c3c' : '#27ae60',
                              color: 'white'
                            }}>
                              {(editedSettings[setting.setting_key] || 'test') === 'live' ? 'LIVE — Real charges!' : 'TEST — No real charges'}
                            </span>
                          </div>
                        ) : (
                          <input
                            type={setting.is_sensitive ? 'password' : 'text'}
                            value={editedSettings[setting.setting_key] || ''}
                            onChange={(e) => handleSettingChange(setting.setting_key, e.target.value)}
                            disabled={readOnlyMode}
                            style={{
                              width: '100%',
                              maxWidth: '500px',
                              padding: '0.5rem',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              fontSize: '1rem'
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {activeTab === 'amp-of-the-day' && (
        <AmpOfTheDayAdmin api={api} readOnly={readOnlyMode} />
      )}
      {activeTab === 'amp-videos' && (
        <AmpVideosAdmin api={api} readOnly={readOnlyMode} />
      )}
      {activeTab === 'pending-review' && (
        <PendingReviewAdmin api={api} />
      )}
    </div>
  );
};

export default SiteAdminScreen;